import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { connect } from 'react-redux';
import { toastr } from 'react-redux-toastr';
import { Button, Card, CardBody, Label } from 'reactstrap';
import Content from '../../../layout/Content';
import Table from '../../../common/Table';
import TableToolbar from '../../../common/TableToolbar';
import Select from '../../../common/forms/select/Select';
import DatePicker from '../../../common/forms/input/DatePicker';
import { breadcrumbsPropTypes } from '../../../common/resource/proptypes/CommonPropTypes';
import { DEPOSIT_MIGRATION_COLUMNS } from '../../../../config/columns';
import {
  DEPOSIT_MIGRATION_BANK,
  DEFAULT_PAGE_SIZE,
  DEFAULT_SORT,
} from '../../../../config/constants';
import { INT_DATE_FORMAT } from '../../../../config/locale';
import {
  DEPOSIT_MIGRATION_PATH,
  NEW_DEPOSIT_MIGRATION_PATH,
} from '../../../../config/paths';
import { tzNormalizeDate } from '../../../../utils/date';
import { enumToSelectOptions } from '../../../../utils/enum';
import {
  getDepositMigrations,
  clearDepositMigrations,
} from '../../../../actions';

const today = tzNormalizeDate({ date: new Date(), format: INT_DATE_FORMAT });

const DEFAULT_FORM_VALUES = {
  bank: DEPOSIT_MIGRATION_BANK.BCP,
  from: today,
  to: today,
};

const toQueryDate = (value) => {
  const [year, month, day] = value.split('-');

  return `${day}/${month}/${year}`;
};

const toQuery = ({ bank, from, to }) => [
  `bankId:${bank.value}`,
  `createDate$${toQueryDate(from)},${toQueryDate(to)}`,
];

const parseDate = (value) => {
  if (!value) return null;

  const [year, month, day] = value.split('-').map(Number);

  return new Date(year, month - 1, day);
};

const DepositMigrations = ({
  breadcrumbs,
  depositMigrations,
  loading,
  dispatchGetDepositMigrations,
  dispatchClearDepositMigrations,
}) => {
  const [formValues, setFormValues] = useState(DEFAULT_FORM_VALUES);
  const [appliedQuery, setAppliedQuery] = useState(
    toQuery(DEFAULT_FORM_VALUES),
  );

  const lastTableQuery = useRef({
    page: 0,
    size: DEFAULT_PAGE_SIZE,
    sort: DEFAULT_SORT,
    query: [],
  });

  useEffect(() => () => dispatchClearDepositMigrations(), []);

  const setValue = (field, value) =>
    setFormValues((previous) => ({ ...previous, [field]: value }));

  const fetchDepositMigrations = (tableQuery, searchQuery = appliedQuery) => {
    lastTableQuery.current = tableQuery;

    dispatchGetDepositMigrations({
      ...tableQuery,
      query: [...searchQuery, ...tableQuery.query],
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!formValues.from || !formValues.to) {
      toastr.error('Error', 'Seleccione la fecha desde y la fecha hasta.');
      return;
    }

    const query = toQuery(formValues);

    setAppliedQuery(query);
    fetchDepositMigrations({ ...lastTableQuery.current, page: 0 }, query);
  };

  const data = depositMigrations.get('content') || [];
  const pages = depositMigrations.get('totalPages') || null;
  const defaultPageSize = depositMigrations.get('size') || DEFAULT_PAGE_SIZE;

  const subtitle = (
    <span>
      Cada fila es un archivo del banco ya procesado. El cruce corre al subirlo
      y no se repite solo: si el archivo llega corregido, hay que anular la
      carga vieja antes de volver a subirlo, porque al recargar las cuentas
      pasan a colgar de la carga nueva y anular la anterior ya no revierte nada.
    </span>
  );

  const toolbar = (
    <TableToolbar
      buttonPath={NEW_DEPOSIT_MIGRATION_PATH}
      buttonText="Cargar estado de cuenta"
    />
  );

  return (
    <Content
      breadcrumbs={breadcrumbs}
      toolbar={toolbar}
      title="Cargas del estado de cuenta"
      subtitle={subtitle}
      content={
        <>
          <Card className="shadow-sm border-0 mb-4">
            <CardBody>
              <form className="row align-items-end" onSubmit={handleSubmit}>
                <div className="col-md-4 mb-2">
                  <Label className="small mb-1">Entidad bancaria</Label>
                  <Select
                    options={enumToSelectOptions(DEPOSIT_MIGRATION_BANK)}
                    input={{
                      value: formValues.bank,
                      onChange: (option) => setValue('bank', option),
                    }}
                  />
                </div>
                <div className="col-md-3 mb-2">
                  <Label className="small mb-1">Fecha desde</Label>
                  <DatePicker
                    input={{
                      value: parseDate(formValues.from),
                      onChange: (date) =>
                        setValue(
                          'from',
                          date
                            ? tzNormalizeDate({ date, format: INT_DATE_FORMAT })
                            : null,
                        ),
                      name: 'from',
                    }}
                    meta={{}}
                  />
                </div>
                <div className="col-md-3 mb-2">
                  <Label className="small mb-1">Fecha hasta</Label>
                  <DatePicker
                    input={{
                      value: parseDate(formValues.to),
                      onChange: (date) =>
                        setValue(
                          'to',
                          date
                            ? tzNormalizeDate({ date, format: INT_DATE_FORMAT })
                            : null,
                        ),
                      name: 'to',
                    }}
                    meta={{}}
                  />
                </div>
                <div className="col-md-2 mb-2">
                  <Button color="primary" size="sm" block type="submit">
                    Buscar
                  </Button>
                </div>
              </form>
            </CardBody>
          </Card>

          <Card className="shadow-sm border-0">
            <Table
              columns={DEPOSIT_MIGRATION_COLUMNS}
              data={data}
              pages={pages}
              defaultPageSize={defaultPageSize}
              loading={loading}
              fetchData={fetchDepositMigrations}
              modelPath={DEPOSIT_MIGRATION_PATH}
              navigateToModelOnRowClick
              filterable
            />
          </Card>
        </>
      }
    />
  );
};

DepositMigrations.propTypes = {
  breadcrumbs: breadcrumbsPropTypes.isRequired,
  depositMigrations: PropTypes.instanceOf(Immutable.Map).isRequired,
  loading: PropTypes.bool,
  dispatchGetDepositMigrations: PropTypes.func.isRequired,
  dispatchClearDepositMigrations: PropTypes.func.isRequired,
};

DepositMigrations.defaultProps = {
  loading: false,
};

const mapStateToProps = ({ SalesUnit }) => ({
  breadcrumbs: [
    ...SalesUnit.UnitHome.get('breadcrumbs'),
    { text: 'Cargas del estado de cuenta', href: '' },
  ],
  depositMigrations: SalesUnit.DepositMigration.getIn(['all', 'content']),
  loading: SalesUnit.DepositMigration.getIn(['all', 'loading']),
});

const mapDispatchToProps = {
  dispatchGetDepositMigrations: getDepositMigrations,
  dispatchClearDepositMigrations: clearDepositMigrations,
};

export { DepositMigrations };
export default connect(mapStateToProps, mapDispatchToProps)(DepositMigrations);
